/**
 * StageAutomationsSummary — o que a etapa faz, em modo LEITURA (2026-10-14).
 *
 * Com funis compartilhados ("Espelhar meus funis para a equipe"), o membro usa
 * os funis, etapas e automações do administrador e não pode alterá-los (o
 * backend recusa). Antes o menu ⋯ simplesmente sumia e o membro não sabia o
 * que acontecia ao mover um lead. Este resumo lê os MESMOS campos que o
 * LabelConfigDrawer edita (auto_dispatch, distribution, entry_tags, rules,
 * outcome, funnel_copy, message_transitions, closing) e os descreve em
 * português, sem nada editável.
 */
import React, { useEffect, useState } from 'react';
import {
  X, Lock, MessageSquare, Sparkles, FileText, UserCheck, Tag, ListChecks,
  Trophy, Copy, Info, ArrowLeftRight, Flag,
} from 'lucide-react';
import assignmentService from '../../services/global/assignmentService';
import contactTagsService from '../../services/global/contactTagsService';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import { lerAtendimento, saidaBloqueada } from './stageAttendance';

const corta = (t, n = 140) => {
  const s = String(t || '').trim();
  return s.length > n ? `${s.slice(0, n)}…` : s;
};

const StageAutomationsSummary = ({ isOpen, onClose, label, colors, funnelLabels = [] }) => {
  const [targets, setTargets] = useState([]);
  const [tags, setTags] = useState([]);
  const { flags: featureFlags } = useFeatureFlags();

  useEffect(() => {
    if (!isOpen) return;
    assignmentService.getTargets()
      .then((d) => setTargets(Array.isArray(d) ? d : (d?.data || [])))
      .catch(() => setTargets([]));
    contactTagsService.listTags()
      .then((d) => setTags(Array.isArray(d) ? d : []))
      .catch(() => setTags([]));
  }, [isOpen]);

  if (!isOpen || !label) return null;

  const nomeAlvo = (t) => {
    if (!t || !t.id) return null;
    const achado = targets.find((x) => x.type === t.type && String(x.id) === String(t.id));
    return achado?.name || t.name || (t.type === 'agent' ? 'um agente' : 'um membro');
  };
  const nomeTag = (id) => tags.find((t) => String(t.id) === String(id))?.name || 'tag removida';
  const nomeEtapa = (id) => funnelLabels.find((l) => String(l.id) === String(id))?.name || 'outra etapa';

  const ad = label.auto_dispatch || {};
  const dist = label.distribution || {};
  const et = label.entry_tags || {};
  const regras = (Array.isArray(label.rules) ? label.rules : [])
    .filter((r) => (r.patterns || []).some((p) => String(p || '').trim()));
  const fc = label.funnel_copy || {};

  const itens = [];

  // 1) Mensagem ao entrar
  if (ad.enabled) {
    let texto;
    let Icon = MessageSquare;
    if (ad.followup_id) {
      texto = 'Inscreve o contato num follow-up configurado pelo administrador.';
    } else if (ad.message_type === 'ai_generated') {
      Icon = Sparkles;
      texto = ad.instruction
        ? `A IA escreve uma mensagem seguindo a instrução: “${corta(ad.instruction)}”`
        : 'A IA escreve uma mensagem para o contato.';
    } else if (ad.message_type === 'template') {
      Icon = FileText;
      texto = `Envia o template aprovado “${ad.template_name || '—'}”.`;
    } else {
      const anexos = Array.isArray(ad.files) && ad.files.length ? ' (com anexo)' : '';
      texto = ad.text ? `Envia a mensagem: “${corta(ad.text)}”${anexos}` : `Envia uma mensagem${anexos}.`;
    }
    if (ad.repeat_on_reentry) texto += ' Repete se o contato voltar para esta etapa.';
    itens.push({ key: 'msg', Icon, titulo: 'Ao entrar nesta etapa', texto });
  } else {
    itens.push({ key: 'msg', Icon: MessageSquare, titulo: 'Ao entrar nesta etapa', texto: 'Nenhuma mensagem automática.', apagado: true });
  }

  // 2) Responsável
  if (dist.enabled) {
    let texto;
    if (dist.mode === 'fixed' && dist.fixed_target) {
      texto = `Passa o contato para ${nomeAlvo(dist.fixed_target)}.`;
    } else {
      const nomes = (dist.pool || []).map(nomeAlvo).filter(Boolean);
      texto = nomes.length
        ? `Distribui em rodízio entre ${nomes.join(', ')}.`
        : 'Distribui em rodízio entre a equipe.';
    }
    texto += ' Só vale para contato sem responsável.';
    itens.push({ key: 'dist', Icon: UserCheck, titulo: 'Responsável', texto });
  } else {
    itens.push({ key: 'dist', Icon: UserCheck, titulo: 'Responsável', texto: 'Não muda o responsável.', apagado: true });
  }

  // 3) Tags
  const add = Array.isArray(et.add) ? et.add : [];
  const rem = Array.isArray(et.remove) ? et.remove : [];
  if (add.length || rem.length) {
    const partes = [];
    if (add.length) partes.push(`adiciona ${add.map(nomeTag).join(', ')}`);
    if (rem.length) partes.push(`remove ${rem.map(nomeTag).join(', ')}`);
    itens.push({ key: 'tags', Icon: Tag, titulo: 'Tags', texto: `${partes.join(' e ')}.`.replace(/^./, (c) => c.toUpperCase()) });
  }

  // 4) Regras por palavra
  if (regras.length) {
    itens.push({
      key: 'rules', Icon: ListChecks, titulo: 'Regras por palavra-chave',
      texto: `${regras.length} ${regras.length === 1 ? 'regra' : 'regras'}: quando o contato escreve certas palavras, o sistema move de etapa, passa para um agente ou marca tags.`,
    });
  }

  // 5) Resultado
  if (label.outcome === 'won' || label.outcome === 'lost') {
    itens.push({
      key: 'outcome', Icon: Trophy, titulo: 'Resultado',
      texto: label.outcome === 'won'
        ? 'Chegar aqui marca o negócio como GANHO.'
        : 'Chegar aqui marca o negócio como PERDIDO.',
    });
  }

  // 6) Cópia para outro funil
  if (fc.enabled && fc.label_id) {
    itens.push({
      key: 'copy', Icon: Copy, titulo: 'Outro funil',
      texto: `Também coloca o contato em “${nomeEtapa(fc.label_id)}”${fc.only_if_empty ? ', se ele ainda não estiver em nenhuma etapa daquele funil' : ''}.`,
    });
  }

  // 7) Etapa por mensagem e 8) encerramento do atendimento (2026-10-22): só
  // com as flags, como na gaveta do administrador.
  const at = lerAtendimento(label);
  const closingUi = Boolean(featureFlags.funnel_stage_closing);
  if (featureFlags.funnel_message_transitions) {
    const partes = [];
    if (at.transitionOnInbound) partes.push(`quando o cliente escreve, vai para “${nomeEtapa(at.transitionOnInbound)}”`);
    if (at.transitionOnOutbound && !saidaBloqueada(closingUi, at.closingEnabled)) {
      partes.push(`quando nós respondemos, vai para “${nomeEtapa(at.transitionOnOutbound)}”`);
    }
    if (partes.length) {
      itens.push({
        key: 'transitions', Icon: ArrowLeftRight, titulo: 'Muda de etapa sozinho',
        texto: `${partes.join('; ')}.`.replace(/^./, (c) => c.toUpperCase()),
      });
    }
  }
  if (closingUi && at.closingEnabled) {
    const partes = [];
    if (at.closingSummary) partes.push('o agente registra o resumo do atendimento nos comentários');
    if (at.closingReasonTagIds.length) {
      partes.push(`marca uma tag de motivo (${at.closingReasonTagIds.map(nomeTag).join(', ')})`);
    }
    if (at.closingReturnToAgent) partes.push('o contato volta para o agente se alguém da equipe atendeu');
    itens.push({
      key: 'closing', Icon: Flag, titulo: 'Encerra o atendimento',
      texto: partes.length ? `Ao chegar aqui, ${partes.join(', ')}.` : 'Ao chegar aqui, o atendimento é encerrado.',
    });
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-label={`Automações da etapa ${label.name}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 520, maxHeight: '85vh', overflowY: 'auto',
          backgroundColor: colors.bgPrimary, borderRadius: 12,
          border: `1px solid ${colors.borderColor}`, boxShadow: colors.shadowLg || '0 12px 40px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderBottom: `1px solid ${colors.borderColor}`,
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: colors.textPrimary }}>
              Automações da etapa
            </span>
            <span style={{ fontSize: 12, color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: 6 }}>
              {label.color && (
                <span style={{ width: 9, height: 9, borderRadius: 3, backgroundColor: label.color, display: 'inline-block' }} />
              )}
              {label.name}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 999,
              backgroundColor: colors.bgSecondary, color: colors.textSecondary,
            }}>
              <Lock size={11} /> Somente leitura
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: colors.textSecondary, padding: 4 }}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {itens.map(({ key, Icon, titulo, texto, apagado }) => (
            <div key={key} style={{ display: 'flex', gap: 10, opacity: apagado ? 0.6 : 1 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                backgroundColor: colors.bgSecondary, color: colors.textSecondary,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={14} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: colors.textPrimary }}>{titulo}</span>
                <span style={{ fontSize: 12.5, color: colors.textSecondary, lineHeight: 1.45 }}>{texto}</span>
              </div>
            </div>
          ))}

          <div style={{
            display: 'flex', gap: 8, marginTop: 4, padding: '10px 12px', borderRadius: 8,
            backgroundColor: colors.bgSecondary, fontSize: 12, color: colors.textSecondary, lineHeight: 1.5,
          }}>
            <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              Os funis, etapas e automações são da empresa e valem para toda a equipe: só o
              administrador altera. Você pode mover contatos, disparar campanha para a sua
              carteira e atribuir responsável.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StageAutomationsSummary;
